import type { MouseEvent } from "react"
import { useCallback, useMemo, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { Link } from "@tanstack/react-router"
import purify from "isomorphic-dompurify"
import {
	IconCheckOutline18 as Check,
	IconPen2Outline18 as Edit2,
	IconFolderOutline18 as Folder,
	IconFolderPlusOutline18 as FolderPlus,
	IconPlusOutline18 as Plus,
	IconTrashOutline18 as Trash,
} from "nucleo-ui-outline-18"

import type { SnippetFolder } from "@productlane/zero/schema"
import { pro } from "@productlane/lib/src/browser"
import {
	AlertDialog,
	Button,
	IconButton,
	NucleoFillIcon,
} from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
} from "@productlane/ui/src/components/internal/dropdown"
import { EllipsisVerticalIcon as MoreVertical } from "@productlane/ui/src/icons/project/ellipsis-icon"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import { CreateSnippetDialog } from "@/components/editor/snippets/create-snippet-dialog"
import { SnippetFolderDialog } from "@/components/editor/snippets/snippet-folder-dialog"
import { MetaInfo } from "@/components/meta-info"
import { RoleCheck } from "@/components/shared/role-check"
import { UpgradeButton } from "@/components/upgrade-button"
import { analytics } from "@/lib/analytics"
import { useRequiredPlans } from "@/lib/plans"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { PlanPill } from "@/screens/settings/plan-pill"
import { SettingsHeader } from "../settings-header"

export interface SnippetInput {
	title: string
	html: string
	id: string
	folderId?: string | null
}

export const emptyInput: SnippetInput = {
	id: "",
	title: "",
	html: "",
	folderId: null,
}

export const UNCATEGORIZED_FOLDER = "uncategorized"

export interface SnippetRow {
	id: string
	title: string
	html: string
	folderId?: string | null
}

export function SnippetCard({
	snippet,
	folders,
	onEdit,
	onDelete,
	onMove,
}: {
	snippet: SnippetRow
	folders: readonly SnippetFolder[]
	onEdit: () => void
	onDelete: () => void
	onMove: (folderId: string | null) => void
}) {
	return (
		<div className="relative rounded-md bg-gray-50 px-4 py-2.5 text-sm ring-1 ring-inset ring-gray-200 dark:bg-gray-850 dark:ring-gray-700/80">
			<div>
				<div className="text-sm font-medium text-primary">{snippet.title}</div>
				<div
					className="mt-1 line-clamp-2 text-xs text-tertiary [&_*]:m-0 [&_*]:inline"
					dangerouslySetInnerHTML={{
						__html: purify.sanitize(snippet.html),
					}}
				/>
			</div>
			<RoleCheck not={["VIEWER"]}>
				<DropdownMenu
					trigger={
						<IconButton
							Icon={MoreVertical}
							variant="ghost"
							iconClassName="fill-current"
							className="absolute right-2 top-2"
						/>
					}
					contentProps={{ side: "bottom", align: "end" }}
				>
					<DropdownMenuGroup>
						<DropdownMenuItem Icon={Edit2} onClick={onEdit}>
							Edit
						</DropdownMenuItem>
						<DropdownMenuSub>
							<DropdownMenuSubTrigger className="gap-3 px-2 text-secondary">
								<Folder className="h-4 w-4 flex-none" />
								Move to folder
							</DropdownMenuSubTrigger>
							<DropdownMenuPortal>
								<DropdownMenuSubContent>
									<DropdownMenuItem
										Icon={!snippet.folderId ? Check : undefined}
										onClick={() => onMove(null)}
									>
										Uncategorized
									</DropdownMenuItem>
									{folders.map((folder) => (
										<DropdownMenuItem
											key={folder.id}
											Icon={snippet.folderId === folder.id ? Check : undefined}
											onClick={() => onMove(folder.id)}
										>
											{folder.name}
										</DropdownMenuItem>
									))}
								</DropdownMenuSubContent>
							</DropdownMenuPortal>
						</DropdownMenuSub>
						<DropdownMenuItem Icon={Trash} onClick={onDelete}>
							Delete
						</DropdownMenuItem>
					</DropdownMenuGroup>
				</DropdownMenu>
			</RoleCheck>
		</div>
	)
}

export const SnippetsPage = () => {
	const { passes: hasRequiredPlan } = useRequiredPlans(pro.andUp)
	const zero = useZero()

	const workspace = useZeroWorkspace()
	const [snippets] = useQuery(queries.snippets())
	const [folders] = useQuery(queries.snippetFolders())

	const prefilledInput = useRef<SnippetInput>(emptyInput)

	const [isDialogOpen, setDialogOpen] = useState(false)
	const [isFolderDialogOpen, setFolderDialogOpen] = useState(false)
	const [folderToRename, setFolderToRename] = useState<SnippetFolder | null>(
		null,
	)
	const [folderIdToDelete, setFolderIdToDelete] = useState<string | boolean>(
		false,
	)

	const countByFolder = useMemo(() => {
		const counts = new Map<string, number>()
		for (const snippet of snippets) {
			const key = snippet.folderId ?? UNCATEGORIZED_FOLDER
			counts.set(key, (counts.get(key) ?? 0) + 1)
		}
		return counts
	}, [snippets])

	const createSnippet = useCallback(() => {
		prefilledInput.current = emptyInput
		setDialogOpen(true)
		analytics.track("create-snippet")
	}, [])

	const closeDialog = useCallback(() => {
		setDialogOpen(false)
		analytics.track("close-snippet-dialog")
	}, [])

	const createFolder = useCallback(() => {
		setFolderToRename(null)
		setFolderDialogOpen(true)
		analytics.track("create-snippet-folder")
	}, [])

	const renameFolder = useCallback(
		(folder: SnippetFolder) => (e: MouseEvent) => {
			e.preventDefault()
			e.stopPropagation()
			setFolderToRename(folder)
			// Defer until the dropdown menu finished closing, otherwise its
			// dismiss teardown closes the freshly opened dialog.
			setTimeout(() => setFolderDialogOpen(true), 0)
			analytics.track("rename-snippet-folder")
		},
		[],
	)

	const deleteFolder = useCallback(
		(folder: SnippetFolder) => (e: MouseEvent) => {
			e.preventDefault()
			e.stopPropagation()
			setFolderIdToDelete(folder.id)
		},
		[],
	)

	if (!workspace) return null

	const renderFolderTile = (folder: SnippetFolder | null) => {
		const isUncategorized = folder === null
		const href = isUncategorized
			? `/settings/macros/${UNCATEGORIZED_FOLDER}`
			: `/settings/macros/${folder.id}`
		const count = isUncategorized
			? (countByFolder.get(UNCATEGORIZED_FOLDER) ?? 0)
			: (countByFolder.get(folder.id) ?? 0)

		return (
			<Link
				key={`snippet-folder-tile-${isUncategorized ? UNCATEGORIZED_FOLDER : folder.id}`}
				to={href}
				className="group relative flex flex-col gap-3 rounded-md bg-gray-50 p-4 ring-1 ring-inset ring-gray-200 hover:bg-gray-150 dark:bg-gray-850 dark:ring-gray-700/80 dark:hover:bg-gray-800"
			>
				{!isUncategorized && folder.icon ? (
					<NucleoFillIcon
						icon={folder.icon}
						className="h-4 w-4 text-secondary"
					/>
				) : (
					<Folder className="h-4 w-4 text-secondary" />
				)}
				<div className="min-w-0">
					<div className="truncate text-sm font-medium text-primary">
						{isUncategorized ? "Uncategorized" : folder.name}
					</div>
					<div className="mt-0.5 text-xs text-tertiary">
						{count} {count === 1 ? "macro" : "macros"}
					</div>
				</div>
				{!isUncategorized && (
					<RoleCheck not={["VIEWER"]}>
						<div className="absolute right-2 top-2">
							<DropdownMenu
								trigger={
									<IconButton
										Icon={MoreVertical}
										variant="ghost"
										iconClassName="fill-current"
										onClick={(e) => {
											e.preventDefault()
											e.stopPropagation()
										}}
									/>
								}
								contentProps={{ side: "bottom", align: "end" }}
							>
								<DropdownMenuGroup>
									<DropdownMenuItem Icon={Edit2} onClick={renameFolder(folder)}>
										Edit
									</DropdownMenuItem>
									<DropdownMenuItem Icon={Trash} onClick={deleteFolder(folder)}>
										Delete
									</DropdownMenuItem>
								</DropdownMenuGroup>
							</DropdownMenu>
						</div>
					</RoleCheck>
				)}
			</Link>
		)
	}

	return (
		<>
			<MetaInfo title="Macros" />

			<CreateSnippetDialog
				data={prefilledInput.current}
				isOpen={isDialogOpen}
				onOpenChange={closeDialog}
			/>

			<SnippetFolderDialog
				isOpen={isFolderDialogOpen}
				onOpenChange={setFolderDialogOpen}
				folder={folderToRename}
			/>

			<div className="space-y-4">
				<SettingsHeader
					title="Macros"
					subtitle="Create pre-written responses to reply faster."
					docsArticle="2d5a2535-0e45-450f-a97d-13318d89124c"
					titleSuffix={<PlanPill plan="pro" />}
				/>

				{hasRequiredPlan ? (
					<>
						<div className="space-y-4">
							<div className="flex items-center justify-between">
								<p className="font-medium">Workspace macros</p>
								<RoleCheck not={["VIEWER"]}>
									<div className="flex items-center gap-2">
										<Button
											Icon={FolderPlus}
											variant="outline"
											onClick={createFolder}
										>
											New folder
										</Button>
										<Button Icon={Plus} onClick={createSnippet}>
											New macro
										</Button>
									</div>
								</RoleCheck>
							</div>

							<div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
								{folders.map((folder) => renderFolderTile(folder))}
								{renderFolderTile(null)}
							</div>
						</div>

						<AlertDialog
							isOpen={!!folderIdToDelete}
							onOpenChange={setFolderIdToDelete}
							confirmText="Delete"
							title="Delete folder?"
							description="Macros in this folder are kept and become uncategorized."
							onCancel={() => setFolderIdToDelete(false)}
							onConfirm={async () => {
								if (typeof folderIdToDelete !== "string") return
								await mutateOk(
									zero.mutate(
										mutators.snippetFolders.delete({
											id: folderIdToDelete,
										}),
									),
								)
								analytics.track("snippet-folder-delete")
							}}
						/>
					</>
				) : (
					<UpgradeButton required={pro.andUp} />
				)}
			</div>
		</>
	)
}
